import {Component, computed, DestroyRef, inject, input, OnInit, output, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CreateTaStep, CreateTaStepType} from '../../../util/app.types';
import {Button} from 'primeng/button';
import {Card} from 'primeng/card';
import {CreateTaStepContainerComponent} from '../create-ta/create-ta-step-container/create-ta-step-container.component';
import {TaAppStore} from '../../../state/clcta-store';
import {CreateTaStepModalComponent} from '../create-ta/create-ta-step-modal/create-ta-step-modal.component';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';
import {PermissionService} from '../../../services/permission.service';
import {ActivatedRoute, UrlSegment} from '@angular/router';
import {TaSubmissionDatasource} from '../../../datasources/ta-submission.datasource';
import {TaSpinnerComponent} from '../taspinner.component/taspinner.component';

@Component({
  selector: 'view-ta',
  imports: [
    Button,
    Card,
    CreateTaStepContainerComponent,
    TaSpinnerComponent,
  ],
  providers: [
    DialogService
  ],
  templateUrl: './view-ta.component.html',
  styleUrl: './view-ta.component.scss'
})
export class ViewTaComponent implements OnInit {
  store = inject(TaAppStore);
  dialogService = inject(DialogService);
  permissionService = inject(PermissionService);
  private route = inject(ActivatedRoute);
  private taSubmissionDatasource = inject(TaSubmissionDatasource);
  private destroyRef = inject(DestroyRef);

  edit = input<boolean>(false);
  stepToEdit?: CreateTaStep = undefined;
  ref: DynamicDialogRef<CreateTaStepModalComponent> | null | undefined;
  // Traveller selected in the itinerary, shared between read-only view and edit modal.
  itineraryTravellerId = signal<string>('');
  dialogClosed = output<void>();

  /** The id from the `/ta/:id/...` route this component is rendered under. */
  private routeTaId = signal<string | undefined>(undefined);

  /**
   * Gates the step list. Compared against the route id rather than just checking for any TA,
   * so a TA left in the store by the previous page cannot render as this one.
   */
  taLoaded = computed<boolean>(() =>
    !!this.routeTaId() && this.store.taSubmission()?.id === this.routeTaId());

  /**
   * The third state the spinner needs: the server answers an unknown or deleted id with 200 and
   * success:false, so without this the view would wait on a TA that is never coming.
   */
  loadFailed = computed<boolean>(() =>
    !!this.routeTaId() && this.taSubmissionDatasource.taLoadError()?.id === this.routeTaId());

  loadErrorMessage = computed<string>(() =>
    this.taSubmissionDatasource.taLoadError()?.message ?? 'This TA could not be loaded.');

  taNumber = computed<string | undefined>(() => this.store.taSubmission()?.taNumber ?? undefined);

  /**
   * Owns the fetch for the whole view. The step containers each call this too, but they are only
   * created once taLoaded() is true, by which point their call is a no-op.
   *
   * Reads the id off route.url rather than the :id param to match clc-manage-ta-page and
   * create-ta-step-container - the latter is also rendered inside a DynamicDialog, where the
   * injected ActivatedRoute is not the /ta/:id one and the param would resolve to null.
   */
  ngOnInit(): void {
    this.route.url
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(segments => {
        const pathParts = segments.map((segment: UrlSegment) => segment.path);
        const id = pathParts[0] === 'ta' && pathParts.length > 1 ? pathParts[1] : undefined;
        this.routeTaId.set(id);
        if (id) {
          this.taSubmissionDatasource.nullLoadServerTaSubmission(id);
        }
      });
  }

  openEditModal(step: CreateTaStep): void {
    this.stepToEdit = step;
    this.ref = this.dialogService.open(CreateTaStepModalComponent, {
      header: 'Editing TA ' + this.taNumber() + ' - ' +  this.stepToEdit?.title,
      inputValues: {
        currentStep: this.stepToEdit,
        isReadOnly: false,
        travellerId: this.itineraryTravellerId(),
      },
      contentStyle: {
        overflow: 'auto'
      },
      modal: true,
      width: '90%',
      focusOnShow: false,
    })
    this.ref?.onChildComponentLoaded.subscribe(modal => modal.travellerId.subscribe(id => this.itineraryTravellerId.set(id)));

    this.ref?.onClose.subscribe((data) => {
      this.stepToEdit = undefined;
      this.dialogClosed.emit();
    });
  }

  canEdit(step: CreateTaStep): boolean {
    if (step.code === 'itinerary') {
      return this.edit() && this.permissionService.canEditItinerary();
    }
    return this.edit() && this.permissionService.canEditTaPrimary();
  }

  protected readonly CreateTaStepType = CreateTaStepType;
}
